import { useTranslation } from "react-i18next";
import type { MonthTileData, MonthTileStatus } from "../../lib/dividends-utils";

interface Props {
  data: MonthTileData;
  isCurrentMonth: boolean;
  onClick: () => void;
}

function fmtEur(cents: number): string {
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(cents / 100);
}

function monthLabel(month: string): string {
  // "2026-07" → "Jul 2026"
  const [year, mm] = month.split("-");
  const monthNames = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
  const idx = Number.parseInt(mm ?? "1", 10) - 1;
  return `${monthNames[idx] ?? "?"} ${year}`;
}

const STATUS_COLOR: Record<MonthTileStatus, string> = {
  PAID: "var(--pos)",
  ANNOUNCED: "var(--accent)",
  FORECAST: "var(--warn)",
  MIXED: "var(--warn)",
};

const STATUS_BG: Record<MonthTileStatus, string> = {
  PAID: "var(--pos-soft)",
  ANNOUNCED: "var(--accent-soft)",
  FORECAST: "var(--warn-soft)",
  MIXED: "var(--warn-soft)",
};

export function MonthTile({ data, isCurrentMonth, onClick }: Props) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left rounded-lg p-3 border transition-transform hover:-translate-y-0.5"
      style={{
        borderColor: isCurrentMonth ? "var(--warn)" : "var(--border)",
        background: "var(--card)",
        borderWidth: isCurrentMonth ? 2 : 1,
      }}
    >
      <div className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: "var(--muted)" }}>
        {monthLabel(data.month)}
      </div>
      <div className="text-lg font-extrabold mt-0.5">{fmtEur(data.totalEur)}</div>
      <div className="mt-1 flex items-center justify-between gap-1">
        <span className="text-[10px]" style={{ color: "var(--muted)" }}>
          {t("dividends.tile.positionsCount", { count: data.positionCount })}
        </span>
        <span
          className="text-[9px] font-bold px-1.5 py-0.5 rounded"
          style={{ color: STATUS_COLOR[data.status], background: STATUS_BG[data.status] }}
        >
          {t(`dividends.status.${data.status}`)}
        </span>
      </div>
    </button>
  );
}
